import ResizableTextarea from '@/components/ResizableTextarea';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import { Quiz } from '@/models/Quiz';
import { QuizQuestion, QuizQuestionForm, QuizQuestionFormStateError } from '@/models/QuizQuestion';
import { QuizResult } from '@/models/QuizResult';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { getQuizQuestionListRequest, getQuizResultListRequest, postQuizQuestionRequest } from '@/store/quiz/action';
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import Answer from './Answer';
import { formErrors, formInitialState, validate } from './formConfig';
import QuizQuestionPreviewScreen from './Preview';
import { QuizModalBody } from './QuizQuestionModal';
import QuizQuestionsPanel from './QuizQuestionsPanel';
import './style.scss';
import { ControllerEvent, FormValue, ModalMode } from './types';

const noop = () => {};

const questionNotChanged = (base: QuizQuestion, state: QuizQuestionForm) => {
    const a = _.pick(base, ['description', 'image', 'options']) as QuizQuestionForm;
    const b = _.omit(state, ['id', 'quiz']) as QuizQuestionForm;
    return _.isEqual(a, b);
};

const CreateQuizQuestionaireScreen: React.FC = () => {
    const { strings, loading, hasError, success, quizContent, quizQuestions, quizResults } = useSelector((state: ApplicationState) => {
        const {
            loading,
            success,
            hasError,
            data: { quizData },
        } = state.quiz;
        return {
            strings: state.locale.data,
            personId: state.identity.data.person.id,
            identity: state.identity.data,
            colors: state.appOrganization.data.colors,
            success,
            hasError,
            loading,
            ...quizData,
            quizResults: state.quiz.data.quizData.quizResults,
        };
    });

    const questions = quizQuestions || [];

    const { id } = useParams<{ id: string }>();
    const uploadComponentRef = useRef<{ clearFileInput: Function }>(null);
    const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.CLOSED);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [imagePreviewFile, setImagePreviewFile] = useState<string>();
    const [controlEvent, setEventControl] = useState<ControllerEvent>(ControllerEvent.NONE);
    const [hasWarningShown, setHasWarningShown] = useState<boolean>(false);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [formState, setFormState] = useState<QuizQuestionForm>(questions[currentQuestionIndex] || formInitialState(0));
    const [{ descriptionError }, setFormError] = useState<QuizQuestionFormStateError>(formErrors);
    const [resultsWithNoQuestions, setResultsWithNoQuestions] = useState<QuizResult[]>([]);
    const dispatch = useDispatch();
    const history = useHistory();

    const quizId = parseInt(id) || null;
    const isEditing = Boolean(questions[currentQuestionIndex]);
    const canDeleteQuestion: Boolean = questions.length > 0;

    const handleOnSetModalMode = (mode: ModalMode) => {
        setModalMode(mode);
    };

    const handleOnChange = (fieldName: string, value: FormValue) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    };

    const handleOnChangeAnswer = (index: number, value: string) => {
        setFormState((prevState) => {
            const option = prevState.options[index];
            prevState.options[index] = { ...option, description: value };
            return { ...prevState };
        });
    };

    const handleOnChangeAnswerResult = (index: number, value: number) => {
        setFormState((prevState) => {
            const options = [...prevState.options];
            const option = options[index];
            const result = quizResults.find(({ id }) => value === id) || null;
            options[index] = { ...option, result: result };
            return { ...prevState, options };
        });
    };

    const validateForm = () => {
        const existingDescriptions = questions.reduce(
            (acc: string[], r: QuizQuestion, index: number) => (index !== currentQuestionIndex && acc.push(r.description), acc),
            [],
        );

        setFormError(formErrors);
        validate(formState, { existingDescriptions, strings })
            .then(() => setFormIsValid(true))
            .catch((err: Record<string, string>) => {
                setFormIsValid(false);
                setFormError((prevState: QuizQuestionFormStateError) => ({ ...prevState, ...err }));
            });
    };

    const back = () => history.push(`/contents/buzz/tests/${id}/results`);

    const unblockFloatControls = () => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.FORWARD,
                blockUi: false,
            } as FloatingCourseActionsBarConfiguration),
        );
    };

    const displayLoading = () =>
        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.LOADING,
                blockUi: true,
            } as FloatingCourseActionsBarConfiguration),
        );

    const addQuestion = () => {
        uploadComponentRef?.current?.clearFileInput();
        setEventControl(ControllerEvent.NONE);
        setCurrentQuestionIndex(questions.length);
        setFormState(formInitialState(questions.length));
        unblockFloatControls();
    };

    const saveQuestion = unblockFloatControls;

    const editQuestion = () => alert('A edição ainda não implementada.');

    const createQuestion = () => {
        const quizQuestionTO = {
            quiz: { id: quizId } as Quiz,
            ...formState,
        } as QuizQuestion;
        dispatch(postQuizQuestionRequest(quizQuestionTO));
    };

    let action = createQuestion;
    const question = questions[currentQuestionIndex] || formInitialState(questions.length);
    const hasQuestionChanged = !questionNotChanged(question, formState);

    if (isEditing) {
        action = hasQuestionChanged ? editQuestion : addQuestion;
    }

    const submit = _.compose(action, displayLoading);

    useEffect(() => {
        if (quizId) {
            dispatch(getQuizQuestionListRequest(quizId));
            dispatch(getQuizResultListRequest(quizId));
        }
    }, [id]);

    const onNextHandler = () => {
        if ((isEditing && quizQuestions.length >= 1 && resultsWithNoQuestions.length === 0) || hasWarningShown) {
            setEventControl(ControllerEvent.NEXT);
        } else {
            setEventControl(ControllerEvent.PENDING);
        }
    };

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 3,
                nextButtonTitle: strings.FORWARD,
                onNext: () => onNextHandler(),
                onPrevious: () => setEventControl(ControllerEvent.PREVIOUS),
                onSave: () => setEventControl(ControllerEvent.SAVE),
                saveDisabled: !isEditing,
                previousButtonTitle: strings.BACK,
                saveButtonTitle: quizContent.id ? strings.SAVE_AND_CLOSE : strings.SAVE_TO_DRAFT,
                totalSteps: 4,
                nextDisabled: !isEditing,
                previewDisabled: false,
                blockUi: false,
            }),
        );
    }, [strings._LANGUAGE, isEditing, resultsWithNoQuestions?.length, quizQuestions?.length, hasWarningShown]);

    useEffect(() => {
        setResultsWithNoQuestions(
            quizResults.filter(
                (res) => !questions.find((ques: QuizQuestion) => ques?.options[1]?.result?.id === res.id || ques?.options[0]?.result?.id === res.id),
            ),
        );
    }, [quizQuestions?.length, quizResults.length]);

    useEffect(() => {
        if (loading) {
            return;
        }

        const currentQuestion: QuizQuestion = questions[currentQuestionIndex] || formInitialState(questions.length);
        setFormState(currentQuestion);
        setImagePreviewFile(question.image || undefined);
    }, [loading, questions.length, currentQuestionIndex]);

    useEffect(validateForm, [formState]);

    const onClickNext = () => {
        alert('Publicação ainda não implementada');
        setEventControl(ControllerEvent.NONE);
    };

    const onPendingHandler = () => {
        if (resultsWithNoQuestions.length > 0 && !hasWarningShown) {
            setModalMode(ModalMode.RESULTS_WITHOUT_QUESTION);
            setHasWarningShown(true);
        }
    };

    const stateControl = {
        [ControllerEvent.NONE]: noop,
        [ControllerEvent.NEXT]: onClickNext,
        [ControllerEvent.PENDING]: onPendingHandler,
        [ControllerEvent.SAVE]: submit,
        [ControllerEvent.SAVE_QUESTION]: submit,
        [ControllerEvent.PREVIOUS]: back,
    };

    // execute an action based on the state.
    useEffect(() => {
        stateControl[controlEvent]();
    }, [controlEvent]);

    // controls which is the next state after a request if completed.
    useEffect(() => {
        if (loading) return;

        const error = !loading && (hasError || !success);
        if (error) {
            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveDisabled: !isEditing,
                    nextButtonTitle: strings.FORWARD,
                    nextDisabled: !isEditing,
                    previewDisabled: false,
                    blockUi: false,
                } as FloatingCourseActionsBarConfiguration),
            );
            setEventControl(ControllerEvent.NONE);
            return;
        }

        if (controlEvent === ControllerEvent.NONE) {
            return;
        }

        if (controlEvent === ControllerEvent.SAVE) {
            addQuestion();
        }

        if (controlEvent === ControllerEvent.SAVE_QUESTION) {
            saveQuestion();
        }
    }, [success, loading, hasError, isEditing, resultsWithNoQuestions]);

    const onCancelHandler = () => {
        if (!questions[currentQuestionIndex]) {
            if (imagePreviewFile) {
                setImagePreviewFile(undefined);
                uploadComponentRef?.current?.clearFileInput();
            }
            setCurrentQuestionIndex(0);
            setFormState(questions[0]);
            setFormIsValid(true);
            unblockFloatControls();
        }
    };

    const onSaveQuestionHandler = () => {
        setEventControl(ControllerEvent.SAVE_QUESTION);
    };

    const onDeleteQuestionHandler = () => alert('Exclusão ainda não implementada.');

    const onCloseResultsWithoutQuestionModal = () => {
        setModalMode(ModalMode.CLOSED);
        setEventControl(ControllerEvent.NONE);
    };

    const onClickQuestion = (questionIndex: number) => {
        setImagePreviewFile(undefined);
        uploadComponentRef?.current?.clearFileInput();
        setCurrentQuestionIndex(questionIndex);
    };

    return (
        <div className="create-quiz-question-container">
            <span className="create-quiz-info__warning">{`* ${strings.REQUIRED_FIELDS}`}</span>
            <div className="create-quiz-question-title-desc">{strings.ADD_QUESTIONS_FOR_TEST}</div>

            <QuizQuestionsPanel
                currentQuestionIndex={currentQuestionIndex}
                hasQuestionChanged={hasQuestionChanged}
                isCurrentQuestionFormValid={formIsValid}
                isEditing={isEditing}
                onClickNewQuestion={() => addQuestion()}
                onClickQuestion={onClickQuestion}
            />

            <hr />
            <div className="create-quiz-question-content">
                <div className="create-quiz-question-form">
                    <div className="create-quiz-header">
                        <div className="create-quiz-question-form-result-number">
                            {strings.QUESTION} {currentQuestionIndex + 1}
                        </div>

                        <div className="d-flex align-items-end">
                            {canDeleteQuestion && isEditing && (
                                <button className="btn btn-link btn--delete" onClick={onDeleteQuestionHandler}>
                                    <span>{strings.DELETE}</span>
                                </button>
                            )}
                            <button className="btn btn-link btn--cancel" disabled={isEditing || !canDeleteQuestion} onClick={onCancelHandler}>
                                <span>{strings.CANCEL}</span>
                            </button>
                            <button
                                className="btn btn--save"
                                disabled={!formIsValid || questionNotChanged(questions[currentQuestionIndex], formState)}
                                onClick={onSaveQuestionHandler}
                            >
                                <span className="save-btn">{strings.SAVE}</span>
                                <i className="material-icons-outlined ">{Constants.icons.SAVE}</i>
                            </button>
                        </div>
                    </div>
                    <div className="input-section margin-bottom-16">
                        <h1>{strings.IMAGE}</h1>
                        <Upload
                            onComplete={(e: FileUploadData) => {
                                handleOnChange('image', e.key);
                                setImagePreviewFile(e.preview);
                            }}
                            onRemove={() => {
                                handleOnChange('image', '');
                                setImagePreviewFile(undefined);
                            }}
                            previewFile={{ uri: formState.image, name: formState.image }}
                            ref={uploadComponentRef}
                            typeAccepted="IMAGE"
                        />
                    </div>
                    <div className="input-section required margin-bottom-16">
                        <h1>{strings.ENUNCIATION}</h1>
                        {descriptionError && <h1 className="error-message">{descriptionError}</h1>}
                        <ResizableTextarea
                            countChars
                            maxLength={160}
                            maxRows={4}
                            minRows={4}
                            placeholder={strings.TYPE_HERE}
                            setValue={(text: string) => handleOnChange('description', text)}
                            textAreaClassName="form-control enunciation-textarea"
                            value={formState.description}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        {['left', 'right'].map((direction: string, index: number) => (
                            <Answer
                                direction={direction}
                                index={index}
                                key={`quiz-answer-${direction}`}
                                onChangeAnswer={handleOnChangeAnswer}
                                onChangeResult={handleOnChangeAnswerResult}
                                onClickToggleHint={handleOnSetModalMode}
                                options={formState.options}
                                {...formState}
                            />
                        ))}
                    </div>
                </div>
                <div className="create-quiz-question-preview">
                    <QuizQuestionPreviewScreen formState={formState} imageTempPath={imagePreviewFile} />
                </div>
            </div>
            <Modal
                backdrop="static"
                centered
                className="create-quiz-question-modal"
                keyboard={false}
                onExited={() => setModalMode(ModalMode.CLOSED)}
                show={modalMode !== ModalMode.CLOSED}
            >
                {modalMode === ModalMode.HINT || modalMode === ModalMode.WHY ? (
                    <QuizModalBody mode={modalMode} setModalMode={setModalMode} />
                ) : (
                    <QuizModalBody mode={modalMode} onClose={onCloseResultsWithoutQuestionModal} resultsWithNoQuestions={resultsWithNoQuestions} />
                )}
            </Modal>
        </div>
    );
};

export default CreateQuizQuestionaireScreen;
